/*
  BOARD QUIZ - Softech Board Selector
  Matches board sizes to the rider using the Guild Factor (GF):
    target volume (L) = GF range for the skill level x rider weight (kg)
  "Fit"        = volume inside the target range
  "Borderline" = within the tolerance % outside it (shown as "Also suits")
  If nothing fits, the closest sizes are shown instead.
*/
(function () {
  'use strict';

  // Treat 7'0", curly/prime quotes and 7' 0" as the same size
  function normaliseSize(s) {
    return String(s || '')
      .replace(/[\u2018\u2019\u2032`]/g, "'")
      .replace(/[\u201C\u201D\u2033]/g, '"')
      .replace(/''/g, '"')
      .replace(/\s+/g, '')
      .toLowerCase();
  }

  // "7'0\" = 70" per line -> { "7'0\"": 70 }
  function parseVolumes(text) {
    var map = {};
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('=');
      if (parts.length < 2) return;
      var litres = parseFloat(parts[1]);
      if (!isNaN(litres)) map[normaliseSize(parts[0])] = litres;
    });
    return map;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // One entry per board size the store sells and we know the volume of
  function buildSizes(board, hideSoldOut) {
    var volumes = parseVolumes(board.volumes);
    var bySize = {};
    var order = [];
    board.variants.forEach(function (v) {
      var key = normaliseSize(v.size);
      if (!(key in volumes)) return;
      if (!bySize[key]) {
        bySize[key] = { label: v.size, litres: volumes[key], variant: null, available: false, price: v.price };
        order.push(key);
      }
      var s = bySize[key];
      // Prefer the first in-stock colour for the size link
      if (v.available && !s.available) {
        s.variant = v; s.available = true; s.price = v.price;
      } else if (!s.variant) {
        s.variant = v;
      }
    });
    return order.map(function (k) { return bySize[k]; }).filter(function (s) {
      return s.available || !hideSoldOut;
    });
  }

  // 0 = inside range; otherwise how far outside, as a fraction of the nearest edge
  function distanceOutside(litres, min, max) {
    if (litres < min) return (min - litres) / min;
    if (litres > max) return (litres - max) / max;
    return 0;
  }

  function recommend(config, skillIdx, weightIdx) {
    var skill = config.skills[skillIdx];
    var kg = parseFloat(config.weights[weightIdx].kg);
    var min = parseFloat(skill.min) * kg;
    var max = parseFloat(skill.max) * kg;
    var mid = (min + max) / 2;
    var tol = (parseFloat(config.tolerance) || 0) / 100;
    var maxBoards = parseInt(config.maxBoards, 10) || 6;

    var all = config.boards.map(function (board) {
      var sizes = buildSizes(board, config.hideSoldOut).map(function (s) {
        var d = distanceOutside(s.litres, min, max);
        s.status = d === 0 ? 'fit' : (d <= tol + 1e-9 ? 'borderline' : 'out');
        s.distance = d;
        s.fromMid = Math.abs(s.litres - mid);
        return s;
      });
      return { board: board, sizes: sizes };
    });

    function rankSizes(a, b) {
      var rank = { fit: 0, borderline: 1, out: 2 };
      return rank[a.status] - rank[b.status] || a.distance - b.distance || a.fromMid - b.fromMid;
    }

    var matches = all.map(function (entry) {
      var good = entry.sizes.filter(function (s) { return s.status !== 'out'; }).sort(rankSizes);
      // List sizes shortest to longest; the best one is highlighted
      var bySize = good.slice().sort(function (a, b) { return a.litres - b.litres; });
      return { board: entry.board, sizes: bySize, best: good[0] };
    }).filter(function (m) { return m.best; });

    var closest = false;
    if (!matches.length) {
      // Nothing fits: show each board's nearest size
      closest = true;
      matches = all.map(function (entry) {
        var sorted = entry.sizes.slice().sort(rankSizes);
        return { board: entry.board, sizes: sorted.slice(0, 1), best: sorted[0] };
      }).filter(function (m) { return m.best; });
    }

    matches.sort(function (a, b) { return rankSizes(a.best, b.best); });
    if (closest) matches = matches.slice(0, 2);

    return {
      boards: matches.slice(0, maxBoards),
      closest: closest,
      range: [Math.round(min), Math.round(max)]
    };
  }

  function renderResults(config, result) {
    var t = config.text;
    if (!result.boards.length) {
      return '<p class="bsq__empty">' + escapeHtml(t.noMatch) + '</p>';
    }
    var html = result.closest ? '<p class="bsq__note">' + escapeHtml(t.noMatch) + '</p>' : '';
    html += '<ul class="bsq__grid">';
    result.boards.forEach(function (m) {
      var b = m.board;
      var badge = result.closest ? t.closest : (m.best.status === 'fit' ? t.bestFit : t.alsoSuits);
      var badgeClass = result.closest || m.best.status !== 'fit' ? 'bsq__badge--alt' : '';
      html += '<li class="bsq__card">';
      html += '<span class="bsq__badge ' + badgeClass + '">' + escapeHtml(badge) + '</span>';
      if (b.image) {
        html += '<a class="bsq__img" href="' + escapeHtml(b.url) + '?variant=' + m.best.variant.id + '">' +
          '<img src="' + escapeHtml(b.image) + '" alt="' + escapeHtml(b.title) + '" loading="lazy"></a>';
      }
      html += '<h3 class="bsq__name">' + escapeHtml(b.title) + '</h3>';
      if (b.blurb) html += '<p class="bsq__blurb">' + escapeHtml(b.blurb) + '</p>';
      html += '<div class="bsq__sizes">';
      m.sizes.forEach(function (s) {
        var cls = 'bsq__size' + (s === m.best ? ' is-best' : '') + (s.available ? '' : ' is-sold-out');
        var label = escapeHtml(t.shop + ' ' + s.label) + ' <span>' + s.litres + 'L \u00b7 ' + escapeHtml(s.available ? s.price : t.soldOut) + '</span>';
        html += '<a class="' + cls + '" href="' + escapeHtml(b.url) + '?variant=' + s.variant.id + '">' + label + '</a>';
      });
      html += '</div></li>';
    });
    return html + '</ul>';
  }

  function initQuiz(root) {
    var configEl = root.querySelector('[data-bsq-config]');
    if (!configEl) return;
    var config;
    try { config = JSON.parse(configEl.textContent); } catch (e) { return; }

    var answers = {};
    var history = [];

    function show(step) {
      root.querySelectorAll('.bsq__step').forEach(function (el) {
        var on = el.getAttribute('data-step') === step;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
      var heading = root.querySelector('[data-step="' + step + '"] .bsq__question');
      if (heading) heading.focus({ preventScroll: true });
      var top = root.getBoundingClientRect().top + window.pageYOffset - 80;
      if (window.pageYOffset > top) window.scrollTo(0, top);
    }

    function go(step) {
      var current = root.querySelector('.bsq__step.is-active');
      if (current) history.push(current.getAttribute('data-step'));
      show(step);
    }

    function showResults() {
      var result = recommend(config, answers.skill, answers.weight);
      root.querySelector('[data-summary]').textContent =
        config.skills[answers.skill].label + ' \u00b7 ' + config.weights[answers.weight].label;
      root.querySelector('[data-results]').innerHTML = renderResults(config, result);
      go('results');

      // Google Tag Manager is on the site; record the quiz result for reporting
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'board_quiz_complete',
        quiz_skill: config.skills[answers.skill].label,
        quiz_weight: config.weights[answers.weight].label,
        quiz_boards: result.boards.map(function (m) { return m.board.title; }).join(', ')
      });
    }

    root.addEventListener('click', function (e) {
      var el = e.target.closest('[data-action], [data-answer]');
      if (!el || !root.contains(el)) return;
      var action = el.getAttribute('data-action');
      var answer = el.getAttribute('data-answer');

      if (action === 'start') go('skill');
      if (action === 'back') show(history.pop() || 'intro');
      if (action === 'restart') { answers = {}; history = []; show('skill'); }

      if (answer) {
        answers[answer] = parseInt(el.getAttribute('data-value'), 10);
        el.parentNode.querySelectorAll('.bsq__option').forEach(function (o) {
          o.classList.toggle('is-selected', o === el);
        });
        if (answer === 'skill') go('weight');
        else showResults();
      }
    });
  }

  function initAll() {
    document.querySelectorAll('.bsq').forEach(initQuiz);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Exposed for the local test page only
  window.BoardSelectorQuiz = { recommend: recommend, normaliseSize: normaliseSize };
})();
